"use client";

import * as React from "react";
import Image from "next/image";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { useSession } from "next-auth/react";
import { ArrowRight, CheckCircle2, Gift } from "lucide-react";
import type { Promotions } from "@prisma/client";

import type { CoreServiceWithPlans } from "@/app/_lib/db/core-service";
import { formatPrice } from "@/app/_lib/utils";
import { cn } from "@/app/_lib/utils";
import { TopoHero } from "@/app/_components/marketing/topo-hero";
import { SerifHeading } from "@/app/_components/marketing/serif-heading";
import { SectionEyebrow } from "@/app/_components/marketing/section-eyebrow";
import { Rail } from "@/app/_components/marketing/rail";
import { ServiceCard } from "@/app/_components/marketing/service-card";
import ServiceModal, { type ServicePromo } from "./service-modal";
import GiftBox from "@/app/_lib/assets/images/gift.png";
type Plan = CoreServiceWithPlans["plans"][0];
type Feature = { key: string; value: string };

const cloud = (id: string) =>
  `https://res.cloudinary.com/${process.env.NEXT_PUBLIC_CLOUDINARY_CLOUD_NAME}/image/upload/${id}`;

const featuresOf = (plan?: Plan | null): Feature[] =>
  Array.isArray(plan?.features) ? (plan!.features as Feature[]) : [];

// ─── Featured pricing column ──────────────────────────────────────────────────
function PricingColumn({
  plan,
  onSelect,
}: {
  plan: Plan;
  onSelect: () => void;
}) {
  const rec = plan.recommended;
  const features = featuresOf(plan);
  return (
    <div
      className={cn(
        "flex flex-col gap-5 p-7",
        rec ? "bg-primary text-primary-foreground" : "",
      )}
    >
      <div>
        <p
          className={cn(
            "text-xs",
            rec ? "text-primary-foreground/70" : "text-muted-foreground",
          )}
        >
          to {plan.name}
        </p>
        <p className="mt-1 text-3xl font-semibold tracking-tight">
          {formatPrice(
            plan.minPrice,
            plan.currency,
            plan.maxPrice ?? undefined,
            plan.priceType as "FIXED" | "RANGE",
          )}
        </p>
        <p
          className={cn(
            "mt-3 text-sm leading-relaxed",
            rec ? "text-primary-foreground/80" : "text-muted-foreground",
          )}
        >
          {plan.description}
        </p>
      </div>

      <div
        className={cn(
          "flex flex-1 flex-col gap-3 border-t pt-5",
          rec ? "border-primary-foreground/20" : "border-border",
        )}
      >
        {features.map((f, i) => (
          <div key={f.key + i} className="flex items-start gap-2.5">
            <CheckCircle2
              size={16}
              className={cn(
                "mt-0.5 shrink-0",
                rec ? "text-primary-foreground" : "text-primary",
              )}
            />
            <p
              className={cn(
                "text-sm leading-snug",
                rec ? "text-primary-foreground/90" : "text-muted-foreground",
              )}
            >
              <span
                className={cn(
                  "font-semibold",
                  rec ? "text-primary-foreground" : "text-foreground",
                )}
              >
                {f.key}
              </span>{" "}
              {f.value}
            </p>
          </div>
        ))}
      </div>

      <button
        onClick={onSelect}
        className={cn(
          "inline-flex items-center justify-center gap-2 rounded-full px-5 py-3 text-sm font-semibold transition hover:opacity-90",
          rec
            ? "bg-background text-primary"
            : "bg-primary text-primary-foreground",
        )}
      >
        Select Plan <ArrowRight size={15} />
      </button>
    </div>
  );
}

// ─── Specialist (Free-Consultation promo) card ────────────────────────────────
function SpecialistCard({
  promo,
  onOpen,
}: {
  promo: ServicePromo;
  onOpen: () => void;
}) {
  return (
    <button
      onClick={onOpen}
      className="group flex flex-col gap-4 border border-border bg-background  text-left transition hover:border-primary/40"
    >
      <h3 className="text-3xl font-semibold leading-snug  p-7">
        {promo.title}
      </h3>
      {promo.image && (
        <div className="relative h-80 overflow-hidden  mt-auto">
          <Image
            src={cloud(promo.image)}
            alt={promo.title}
            fill
            sizes="(min-width: 640px) 50vw, 100vw"
            className="object-contain transition-transform duration-700 group-hover:scale-105"
          />
          <span className="inline-flex absolute bottom-5 left-5 items-center gap-1.5 self-start rounded-full bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground">
            Let&apos;s talk <ArrowRight size={14} />
          </span>
        </div>
      )}
    </button>
  );
}

export default function CoreServicesListing({
  featured,
  others,
  specialist,
}: {
  featured: CoreServiceWithPlans | null;
  others: CoreServiceWithPlans[];
  specialist: Promotions | null;
}) {
  const [selected, setSelected] = React.useState<{
    service?: CoreServiceWithPlans | null;
    plan?: Plan | null;
    promo?: ServicePromo | null;
  } | null>(null);

  const router = useRouter();
  const { data: session } = useSession();
  const isLoggedIn = !!session?.user;

  const goToOffers = () => {
    if (isLoggedIn) {
      router.push("/dashboard/promotions");
    } else {
      router.push("/login?callbackUrl=/dashboard/promotions");
    }
  };

  const specialistPromo: ServicePromo | null = specialist
    ? {
        title: specialist.title,
        description: specialist.description,
        image: specialist.image,
      }
    : {
        title: "Discuss your project one on one with a Specialist",
        description:
          "Book a free consultation and get honest, expert guidance on your next build.",
      };

  return (
    <div>
      {/* Hero */}
      <TopoHero>
        <SerifHeading as="h1" className="font-medium">
          Services.
        </SerifHeading>
        <p className="mx-auto mt-4 max-w-lg text-sm text-muted-foreground">
          Everything you need to build, grow, and sustain your digital presence.
        </p>
      </TopoHero>

      {/* 01 — Featured Service */}
      {featured && (
        <Rail id="featured" innerClassName="py-16 md:py-24" as="div">
          <div className="mx-auto  text-center">
            <SectionEyebrow
              number="01"
              lineClassName="hidden"
              className="justify-center capitalize tracking-normal"
            >
              Featured Service
            </SectionEyebrow>
            <div className="relative mx-auto mt-6 h-48 w-full max-w-md overflow-hidden bg-mint">
              <Image
                src={cloud(featured.image)}
                alt={featured.title}
                fill
                sizes="(min-width: 448px) 448px, 100vw"
                className="object-cover"
              />
            </div>
            <h2 className="mt-6 text-[28px] font-semibold tracking-tight text-foreground">
              {featured.title}
            </h2>
            <p className="mx-auto mt-3 max-w-4xl text-sm leading-relaxed text-[#707070]">
              {featured.description}
            </p>
          </div>

          {featured.plans.length > 0 && (
            <div
              className={cn(
                "mx-auto mt-12 grid max-w-5xl gap-5",
                featured.plans.length === 1
                  ? "max-w-sm"
                  : featured.plans.length === 2
                    ? "md:grid-cols-2"
                    : "md:grid-cols-3",
              )}
            >
              {featured.plans.map((plan) => (
                <PricingColumn
                  key={plan.id}
                  plan={plan}
                  onSelect={() => setSelected({ service: featured, plan })}
                />
              ))}
            </div>
          )}
        </Rail>
      )}

      {/* More Services */}
      {(others.length > 0 || specialistPromo) && (
        <Rail innerClassName="border-t py-16 md:py-24">
          <SectionEyebrow className="justify-center">
            More Services
          </SectionEyebrow>
          <div className="mt-10 grid gap-5 sm:gap-10 sm:grid-cols-2">
            {others.map((service) => (
              <ServiceCard
                key={service.id}
                service={service}
                onOpen={() => setSelected({ service, plan: null })}
              />
            ))}
            {specialistPromo && (
              <SpecialistCard
                promo={specialistPromo}
                onOpen={() => setSelected({ promo: specialistPromo })}
              />
            )}
          </div>
        </Rail>
      )}

      {/* My Offers */}
      <Rail bottom innerClassName="border-t py-16">
        <div className="flex flex-col items-center border border-primary/30 gap-6 px-6 py-12 text-center md:flex-row md:justify-between md:text-left ">
          <div className="flex flex-col items-center gap-5 text-center sm:flex-row sm:gap-12 sm:text-left">
            <Image
              src={GiftBox}
              alt="Gift box"
              width={700}
              height={700}
              className="h-[170px] w-[170px] object-cover object-center"
            />

            <div className="flex flex-col gap-2">
              <h3 className="text-3xl leading-snug">
                <span className="font-semibold"> Exclusive offers.</span>{" "}
                <span className="text-muted-foreground">
                  Updated regularly.
                </span>
              </h3>
              <p className="mt-1 text-base text-muted-foreground">
                Explore our latest offers on web apps, mobile apps, product
                care, and more. Every offer is available for a limited time and
                may change as new opportunities become available.
              </p>
              <button
                type="button"
                onClick={goToOffers}
                className="inline-flex mt-5 items-center w-fit gap-2 rounded-full bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground transition hover:opacity-90"
              >
                See What&apos;s Available <ArrowRight size={15} />
              </button>
            </div>
          </div>
        </div>
      </Rail>

      <ServiceModal
        open={!!selected}
        onClose={() => setSelected(null)}
        service={selected?.service}
        plan={selected?.plan}
        promo={selected?.promo}
      />
    </div>
  );
}
